import {
  Box,
  Button,
  Grid,
  GridItem,
  Heading,
  useToast,
  Select,
  Checkbox,
  Text,
  FormLabel,
  Flex,
  Input,
  FormControl,
  List,
  ListItem,
  Spinner,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback, useRef } from "react";
import states from "../../../constants/states.json";
import { fetchAllVehiclesList } from "../../../actions/management.action";
import { useFormik } from "formik";
import * as Yup from "yup";
import LabelInput from "../../../components/Input";
import InputAndDragAndDrop from "../../../components/InputWithDragAndDrop";
import { Link } from "react-router-dom";
import { fetchSuggestions } from "../../../actions/location.actions";
import { checkDriverPhone } from "../../../actions/inventory/driver.action";
import {
  DRIVER_OPERATIONAL_STATUS,
  DRIVER_OPERATIONAL_STATUS_LABEL,
  DRIVER_OPERATIONAL_STATUS_OPTIONS,
} from "../../../constants/driverStatus";

// ─── Constants ────────────────────────────────────────────────────────────────

const TODAY = new Date();
TODAY.setHours(0, 0, 0, 0);

const MIN_DOB = new Date();
MIN_DOB.setFullYear(MIN_DOB.getFullYear() - 70); // max 70 years old

const MAX_DOB = new Date();
MAX_DOB.setFullYear(MAX_DOB.getFullYear() - 18); // min 18 years old

// ─── Regex Patterns ───────────────────────────────────────────────────────────

// Indian DL: State code (2 letters) + district (2 digits) + optional space/hyphen
// + year (4 digits) + sequence (7 digits) e.g. DL05 20230012345 or MH0120200012345
const DL_REGEX = /^[A-Z]{2}[-\s]?\d{2}[-\s]?\d{4}[-\s]?\d{7}$/i;

// PAN: 5 letters + 4 digits + 1 letter e.g. ABCDE1234F
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

// IFSC: 4 letters (bank code) + 0 + 6 alphanumeric e.g. HDFC0001234
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

// Indian mobile: starts with 6, 7, 8, or 9 — exactly 10 digits
const MOBILE_REGEX = /^[6-9]\d{9}$/;

// Letters and spaces only
const LETTERS_ONLY_REGEX = /^[A-Za-z\s,]+$/;

// Email: requires valid format with proper domain and TLD (min 2 chars)
const EMAIL_REGEX = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9\-]+(\.[a-zA-Z0-9\-]+)*\.[a-zA-Z]{2,}$/;

// ─── Validation Schema ────────────────────────────────────────────────────────

const validationSchema = Yup.object({
  // Step 1 — Driver Info
  uniqueDriverId: Yup.string().required("Driver ID is required"),

  name: Yup.string()
    .required("Driver name is required")
    .min(2, "Name must be at least 2 characters")
    .max(50, "Driver name must not exceed 50 characters")
    .matches(LETTERS_ONLY_REGEX, "Name should only contain letters and spaces — no numbers"),

  dob: Yup.date()
    .required("Date of birth is required")
    .typeError("Please enter a valid date")
    .min(MIN_DOB, "Driver cannot be older than 70 years")
    .max(MAX_DOB, "Driver must be at least 18 years old"),

  // Step 2 — Contact
  email: Yup.string()
    .required("Email is required")
    .matches(EMAIL_REGEX, "Enter a valid email address e.g. name@gmail.com"),

  mobileNumber: Yup.string()
    .required("Mobile number is required")
    .matches(MOBILE_REGEX, "Enter a valid 10-digit Indian mobile number starting with 6-9"),

  whatsappNumber: Yup.string()
    .required("WhatsApp number is required")
    .matches(MOBILE_REGEX, "Enter a valid 10-digit Indian mobile number starting with 6-9"),

  address: Yup.string()
    .trim()
    .required("Address is required")
    .min(10, "Please enter a complete address (min 10 characters)")
    .max(50, "Address must not exceed 50 characters")
    .matches(
      /^[A-Za-z0-9\s,./#-]+$/,
      "Address can only contain letters, numbers, spaces, commas, slashes, periods, hashes, and hyphens",
    )
    .test(
      "address-has-alphanumeric",
      "Address must contain at least one letter or number",
      (value) => !value || /[A-Za-z0-9]/.test(value),
    ),

  city: Yup.string()
    .required("City is required")
    .min(2, "City name must be at least 2 characters")
    .max(50, "City name must not exceed 50 characters")
    .matches(LETTERS_ONLY_REGEX, "City should only contain letters and spaces"),

  state: Yup.string().required("State is required"),

  pincode: Yup.string()
    .required("Pincode is required")
    .matches(/^\d{6}$/, "Pincode must be exactly 6 digits"),

  // Step 3 — Emergency Contact (optional but validated if filled)
  emergencyContactName: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must not exceed 50 characters")
    .matches(LETTERS_ONLY_REGEX, "Name should only contain letters and spaces")
    .optional(),

  emergencyContactNumber: Yup.string()
    .matches(MOBILE_REGEX, "Enter a valid 10-digit Indian mobile number starting with 6-9")
    .optional(),

  emergencyContactRelationship: Yup.string()
    .min(2, "Relationship must be at least 2 characters")
    .max(50, "Relationship must not exceed 50 characters")
    .matches(LETTERS_ONLY_REGEX, "Relationship should only contain letters")
    .optional(),

  // Step 4 — License & Docs
  drivingLicenseNumber: Yup.string()
    .required("Driving license number is required")
    .matches(
      DL_REGEX,
      "Invalid format. Examples: DL0520230012345 or DL05 20230012345 or MH-01-2020-0012345"
    ),

  drivingLicenseExpiryDate: Yup.date()
    .typeError("Please enter a valid date")
    .min(TODAY, "License expiry date must be a future date")
    .optional(),

  aadhaarCardNumber: Yup.string()
    .required("Aadhaar number is required")
    .matches(/^\d{12}$/, "Aadhaar number must be exactly 12 digits"),

  panCardNumber: Yup.string()
    .required("PAN number is required")
    .matches(PAN_REGEX, "Invalid PAN format. Use: ABCDE1234F (5 letters + 4 digits + 1 letter)"),

  // Bank Details (optional but validated if filled)
  accountHolderName: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must not exceed 50 characters")
    .matches(LETTERS_ONLY_REGEX, "Account holder name should only contain letters and spaces")
    .optional(),

  accountNumber: Yup.string()
    .matches(/^\d{9,18}$/, "Account number must be between 9 and 18 digits")
    .optional(),

  ifscCode: Yup.string()
    .matches(IFSC_REGEX, "Invalid IFSC format. Use: HDFC0001234 (4 letters + 0 + 6 alphanumeric)")
    .optional(),

  bankName: Yup.string()
    .min(2, "Bank name must be at least 2 characters")
    .max(50, "Bank name must not exceed 50 characters")
    .matches(LETTERS_ONLY_REGEX, "Bank name should only contain letters and spaces")
    .optional(),

  // Step 5 — Additional
  driverExperience: Yup.string()
    .matches(/^\d{1,2}$/, "Experience must be a number between 0 and 50")
    .test("max-exp", "Experience cannot exceed 50 years", (val) => !val || parseInt(val) <= 50)
    .optional(),

  education: Yup.string()
    .max(50, "Education details must not exceed 50 characters")
    .matches(/^[A-Za-z0-9\s]+$/, "Education can only contain letters, numbers, and spaces")
    .optional(),
  // Step 7 — Status
  operationalStatus: Yup.string().required("Operational status is required"),
  accountStatus: Yup.string().required("Account status is required"),
  additionalDetails: Yup.string().max(50, "Additional details must not exceed 50 characters").optional(),
});

// ─── Inline Error Component ───────────────────────────────────────────────────

const FieldError = ({ error, touched }) =>
  error && touched ? (
    <Text color="red.500" fontSize="xs" mt={1}>
      {error}
    </Text>
  ) : null;

// ─── Main Form Component ──────────────────────────────────────────────────────

const Form = ({ data, setData, handleSubmit, formRef, validationSchema: externalValidationSchema, isEditMode = false }) => {
  const toast = useToast();
  const toastIdRef = useRef({});
  const [isSameAsMobile, setIsSameAsMobile] = useState(false);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [loadingCity, setLoadingCity] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const debounceTimerRef = useRef(null);
  const [mobileCheckError, setMobileCheckError] = useState("");
  const [checkingMobile, setCheckingMobile] = useState(false);
  const [duplicateDriverId, setDuplicateDriverId] = useState(null);
  const [duplicateDriverName, setDuplicateDriverName] = useState(null);

  // Single-instance toast — prevents stacking on rapid clicks
  const showToastOnce = (key, options) => {
    if (!toastIdRef.current[key] || !toast.isActive(toastIdRef.current[key])) {
      toastIdRef.current[key] = toast({ ...options });
    }
  };

  const generateUniqueDriverId = () => {
    const randomPart = Math.random().toString(36).slice(2, 7).toUpperCase();
    return `DRV-${randomPart}`;
  };

  const initValues = {
    uniqueDriverId: data?.uniqueDriverId || "",
    name: data?.name || "",
    dob: data?.dob || "",
    email: data?.email || "",
    mobileNumber: data?.mobileNumber || "",
    whatsappNumber: data?.whatsappNumber || "",
    address: data?.address || "",
    city: data?.city || "",
    state: data?.state || "",
    pincode: data?.pincode || "",
    emergencyContactName: data?.emergencyContactName || "",
    emergencyContactNumber: data?.emergencyContactNumber || "",
    emergencyContactRelationship: data?.emergencyContactRelationship || "",
    accountHolderName: data?.accountHolderName || "",
    accountNumber: data?.accountNumber || "",
    drivingLicenseFront: data?.drivingLicenseFront || "",
    drivingLicenseBack: data?.drivingLicenseBack || "",
    aadhaarCardFront: data?.aadhaarCardFront || "",
    aadhaarCardBack: data?.aadhaarCardBack || "",
    panCardFront: data?.panCardFront || "",
    ifscCode: data?.ifscCode || "",
    bankName: data?.bankName || "",
    drivingLicenseNumber: data?.drivingLicenseNumber || "",
    drivingLicenseExpiryDate: data?.drivingLicenseExpiryDate || "",
    drivingLicenseType: data?.drivingLicenseType || "",
    aadhaarCardNumber: data?.aadhaarCardNumber || "",
    panCardNumber: data?.panCardNumber || "",
    licenseHillEndorsement: data?.licenseHillEndorsement || false,
    isVerified: data?.isVerified || false,
    driverExperience: data?.driverExperience || "",
    languagesKnown: data?.languagesKnown || "",
    education: data?.education || "",
    operationalStatus: data?.operationalStatus || DRIVER_OPERATIONAL_STATUS.UNASSIGNED,
    accountStatus: data?.accountStatus || "active",
  };

  const resolvedValidationSchema = externalValidationSchema || validationSchema;

  const formik = useFormik({
    initialValues: initValues,
    validationSchema: resolvedValidationSchema,
    enableReinitialize: true,
    validateOnChange: true,  // real-time validation
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await handleSubmit(values);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, errors, touched, handleBlur, setFieldValue, setFieldTouched } = formik;

  // ─── Mobile Number Duplicate Check (Add mode only) ────────────────────────

  const handleMobileBlur = async (e) => {
    // Always run Formik's built-in blur handler first
    handleBlur(e);

    // Skip duplicate check in edit mode
    if (isEditMode) return;

    const mobile = e.target.value;
    setMobileCheckError("");
    setDuplicateDriverId(null);
    setDuplicateDriverName(null);

    // Only check if it's a valid 10-digit Indian mobile number
    if (!mobile || mobile.length !== 10 || !/^[6-9]\d{9}$/.test(mobile)) return;

    try {
      setCheckingMobile(true);
      const result = await checkDriverPhone(mobile);
      if (result.exists) {
        setMobileCheckError("Driver with this number already exists");
        if (result.driver?.id) {
          setDuplicateDriverId(result.driver.id);
        }
        if (result.driver?.name) {
          setDuplicateDriverName(result.driver.name);
        }
      }
    } catch (err) {
      console.error("Phone check failed:", err);
    } finally {
      setCheckingMobile(false);
    }
  };

  // ─── Submit Handler ────────────────────────────────────────────────────────

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (mobileCheckError) {
      showToastOnce("mobile-duplicate", {
        title: "Duplicate Mobile Number",
        description: duplicateDriverId ? (
          <Box>
            {mobileCheckError}
            {duplicateDriverName && duplicateDriverId && (
              <Text as="span">
                {" ("}
                <Link to={`/fleet-management/driver/${duplicateDriverId}`} style={{ textDecoration: 'underline', fontWeight: 'bold' }}>
                  {duplicateDriverName}
                </Link>
                {")"}
              </Text>
            )}
          </Box>
        ) : (
          mobileCheckError
        ),
        status: "error",
        duration: 8000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors).length > 0) {
      formik.setTouched(
        Object.keys(validationErrors).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
        true
      );
      const getFirstMsg = (obj) => {
        if (!obj) return null;
        if (typeof obj === "string") return obj;
        if (Array.isArray(obj)) return obj.find((e) => getFirstMsg(e)) || null;
        if (typeof obj === "object") {
          for (const key of Object.keys(obj)) {
            const msg = getFirstMsg(obj[key]);
            if (msg) return msg;
          }
        }
        return null;
      };
      showToastOnce("submit-error", {
        title: "Please fix the errors before submitting",
        description: getFirstMsg(validationErrors) || "Some fields are missing or invalid.",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }
    await formik.submitForm();
  };

  // ─── Field Handlers ────────────────────────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    let v = value;

    // Letters only — silently strip digits as user types; cap specific fields at 50 chars
    if (["name", "emergencyContactName", "accountHolderName", "bankName", "emergencyContactRelationship"].includes(name)) {
      v = value.replace(/[^A-Za-z\s]/g, "");
      if (["name", "emergencyContactRelationship"].includes(name)) v = v.slice(0, 50);
    }


    // city and education — cap at 50 chars
    if (name === "city") v = v.slice(0, 50);
    if (name === "education") v = v.slice(0, 50);

    // Phone numbers — digits only, max 10
    if (["mobileNumber", "whatsappNumber", "emergencyContactNumber"].includes(name)) {
      v = value.replace(/\D/g, "").slice(0, 10);
    }

    // Pincode — digits only, max 6
    if (name === "pincode") {
      v = value.replace(/\D/g, "").slice(0, 6);
    }

    // Aadhaar — digits only, max 12
    if (name === "aadhaarCardNumber") {
      v = value.replace(/\D/g, "").slice(0, 12);
    }

    // PAN — uppercase, max 10, alphanumeric only
    if (name === "panCardNumber") {
      v = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
    }

    // IFSC — uppercase, max 11
    if (name === "ifscCode") {
      v = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11);
    }

    // Account number — digits only, max 18
    if (name === "accountNumber") {
      v = value.replace(/\D/g, "").slice(0, 18);
    }

    // Driver experience — digits only, max 2 chars
    if (name === "driverExperience") {
      v = value.replace(/\D/g, "").slice(0, 2);
    }

    // Address: allow common postal characters, block symbols like @@@@
    if (name === "address") {
      v = value.replace(/[^A-Za-z0-9\s,./#-]/g, "").slice(0, 50);
    }

    // Driving license — uppercase
    if (name === "drivingLicenseNumber") {
      v = value.toUpperCase();
    }

    setFieldValue(name, v);
    setFieldTouched(name, true, false);

    // Sync whatsapp if checkbox is checked
    if (name === "mobileNumber" && isSameAsMobile) {
      setFieldValue("whatsappNumber", v);
    }
  };

  // ─── City Autocomplete ─────────────────────────────────────────────────────

  const handleCityChange = useCallback(
    (value) => {
      setFieldValue("city", value.slice(0, 50));
      setFieldTouched("city", true, false);

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      if (!value.trim()) {
        setCitySuggestions([]);
        setShowCityDropdown(false);
        return;
      }

      debounceTimerRef.current = setTimeout(async () => {
        setLoadingCity(true);
        setShowCityDropdown(true);
        try {
          const response = await fetchSuggestions(value, "cities");
          setCitySuggestions(response?.data || []);
        } catch (err) {
          console.error("City fetch error:", err);
        } finally {
          setLoadingCity(false);
        }
      }, 300);
    },
    [setFieldValue, setFieldTouched]
  );

  const handleCitySelect = (location) => {
    setFieldValue("city", location.description || location.name);
    setShowCityDropdown(false);
    setCitySuggestions([]);
  };

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (
      values.mobileNumber &&
      values.whatsappNumber &&
      values.mobileNumber === values.whatsappNumber
    ) {
      setIsSameAsMobile(true);
    }
  }, [values.mobileNumber, values.whatsappNumber]);

  useEffect(() => {
    if (!values.uniqueDriverId) {
      setFieldValue("uniqueDriverId", generateUniqueDriverId());
    }
  }, []);

  useEffect(() => {
    fetchAllVehiclesList().catch(() =>
      showToastOnce("fetch-vehicles", {
        title: "Error",
        description: "Unable to fetch Vehicle Segment",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    );
  }, []);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      <form ref={formRef} onSubmit={handleFormSubmit}>

        {/* ── Step 1: Driver Information ── */}
        <Box px={6} py={8} borderRadius="lg" bg="white" my="2rem">
          <Heading fontSize="lg" mb={1} color="#1A202C">Driver Information</Heading>
          <Flex justifyContent="space-between" fontSize="0.875rem" letterSpacing={0} mb={6}>
            <Text color="#90A3BF">Please enter basic info</Text>
            <Text color="#90A3BF">Step 1 of 7</Text>
          </Flex>

          <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4} mb={6}>
            <GridItem>
              <LabelInput
                placeholder="DRV-XXXXX"
                label="Driver Unique ID"
                name="uniqueDriverId"
                isRequired
                value={values.uniqueDriverId}
                onBlur={handleBlur}
                onChange={handleChange}
                isReadOnly
                bg={values.uniqueDriverId ? "gray.100" : "white"}
              />
              <FieldError error={errors.uniqueDriverId} touched={touched.uniqueDriverId} />
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="Enter Driver Name"
                label="Driver Name"
                name="name"
                isRequired
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                bg={values.name ? "gray.100" : "white"}
              />
              <FieldError error={errors.name} touched={touched.name} />
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="Enter Date of Birth"
                label="Date of Birth"
                name="dob"
                type="date"
                isRequired
                value={values.dob}
                onChange={handleChange}
                onBlur={handleBlur}
                bg={values.dob ? "gray.100" : "white"}
                // Limit date picker to valid range
                max={MAX_DOB.toISOString().split("T")[0]}
                min={MIN_DOB.toISOString().split("T")[0]}
              />
              <FieldError error={errors.dob} touched={touched.dob} />
            </GridItem>
          </Grid>
        </Box>

        {/* ── Step 2: Contact Details ── */}
        <Box px={6} py={8} borderRadius="lg" bg="white" my="2rem">
          <Heading fontSize="lg" mb={1} color="#1A202C">Contact Details</Heading>
          <Flex justifyContent="space-between" fontSize="0.875rem" letterSpacing={0} mb={6}>
            <Text color="#90A3BF">Please enter contact information</Text>
            <Text color="#90A3BF">Step 2 of 7</Text>
          </Flex>

          <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4} mb={6} alignItems="flex-start">
            <GridItem>
              <LabelInput
                placeholder="Enter Email"
                label="Email"
                name="email"
                isRequired
                type="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                bg={values.email ? "gray.100" : "white"}
              />
              <FieldError error={errors.email} touched={touched.email} />
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="Enter Mobile Number"
                label="Mobile Number"
                name="mobileNumber"
                isRequired
                value={values.mobileNumber}
                inputMode="numeric"
                onChange={(e) => {
                  handleChange(e);
                  // Clear duplicate error when user starts editing again
                  if (mobileCheckError) {
                    setMobileCheckError("");
                    setDuplicateDriverId(null);
                    setDuplicateDriverName(null);
                  }
                }}
                onBlur={handleMobileBlur}
                bg={values.mobileNumber ? "gray.100" : "white"}
              />
              {checkingMobile && (
                <Flex align="center" mt={1}>
                  <Spinner size="xs" mr={2} color="blue.400" />
                  <Text fontSize="xs" color="blue.400">Checking...</Text>
                </Flex>
              )}
              {mobileCheckError && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {mobileCheckError}
                  {duplicateDriverName && duplicateDriverId && (
                    <Text as="span">
                      {" ("}
                      <Link to={`/fleet-management/driver/${duplicateDriverId}`} style={{ textDecoration: 'underline', fontWeight: 'bold' }}>
                        {duplicateDriverName}
                      </Link>
                      {")"}
                    </Text>
                  )}
                </Text>
              )}
              <FieldError error={errors.mobileNumber} touched={touched.mobileNumber} />
            </GridItem>

            <GridItem>
              <Flex justify="space-between" align="center" mb={2}>
                <FormLabel fontSize="1rem" color="#1A202C" mb={0}>WhatsApp Number</FormLabel>
                <Checkbox
                  size="md"
                  isChecked={isSameAsMobile}
                  onChange={(e) => {
                    setIsSameAsMobile(e.target.checked);
                    setFieldValue("whatsappNumber", e.target.checked ? values.mobileNumber : "");
                  }}
                >
                  <Text fontSize="xs">Same as Mobile</Text>
                </Checkbox>
              </Flex>
              <Input
                placeholder="Enter WhatsApp Number"
                name="whatsappNumber"
                isRequired
                value={values.whatsappNumber}
                inputMode="numeric"
                onChange={handleChange}
                onBlur={handleBlur}
                isReadOnly={isSameAsMobile}
                _placeholder={{ fontSize: "sm", color: "#90A3BF" }}
                variant="outline"
                borderRadius="lg"
                bg={values.whatsappNumber ? "gray.100" : "white"}
                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                w="100%"
                h="56px"
              />
              <FieldError error={errors.whatsappNumber} touched={touched.whatsappNumber} />
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="1/234, Sample Street, Area"
                label="Address"
                name="address"
                isRequired
                value={values.address}
                onChange={handleChange}
                onBlur={handleBlur}
                bg={values.address ? "gray.100" : "white"}
              />
              <FieldError error={errors.address} touched={touched.address} />
            </GridItem>

            {/* City with autocomplete */}
            <GridItem>
              <Box position="relative">
                <FormLabel fontSize="1rem" color="#1A202C" htmlFor="city">City</FormLabel>
                <Input
                  placeholder="Enter City"
                  name="city"
                  id="city"
                  value={values.city || ""}
                  onChange={(e) => handleCityChange(e.target.value)}
                  onFocus={() => setShowCityDropdown(true)}
                  onBlur={() => {
                    handleBlur({ target: { name: "city" } });
                    setTimeout(() => setShowCityDropdown(false), 200);
                  }}
                  autoComplete="off"
                  bg={values.city ? "gray.100" : "white"}
                  variant="outline"
                  borderRadius="lg"
                  h="56px"
                  _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                />
                {loadingCity && (
                  <Spinner size="sm" position="absolute" right="10px" top="50%" transform="translateY(-50%)" />
                )}
                {showCityDropdown && citySuggestions.length > 0 && (
                  <List
                    position="absolute"
                    zIndex={10}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    width="100%"
                    maxHeight="240px"
                    overflowY="auto"
                    mt={1}
                    boxShadow="lg"
                  >
                    {citySuggestions.map((location, index) => (
                      <ListItem
                        key={index}
                        px={4}
                        py={2.5}
                        cursor="pointer"
                        _hover={{ bg: "blue.50" }}
                        onMouseDown={() => handleCitySelect(location)}
                      >
                        {location.description || location.name}
                      </ListItem>
                    ))}
                  </List>
                )}
                <FieldError error={errors.city} touched={touched.city} />
              </Box>
            </GridItem>

            {/* state */}
            <GridItem>
              <FormLabel fontSize="1rem" color="#1A202C" htmlFor="state">State</FormLabel>
              <Select
                placeholder="Select State"
                name="state"
                id="state"
                isRequired
                value={values.state}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outline"
                borderRadius="lg"
                bg={values.state ? "gray.100" : "white"}
                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                w="100%"
                h="56px"
              >
                {states.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </Select>
              <FieldError error={errors.state} touched={touched.state} />
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="Enter Pincode"
                label="Pincode"
                name="pincode"
                isRequired
                value={values.pincode}
                onChange={handleChange}
                onBlur={handleBlur}
                inputMode="numeric"
                bg={values.pincode ? "gray.100" : "white"}
              />
              <FieldError error={errors.pincode} touched={touched.pincode} />
            </GridItem>
          </Grid>
        </Box>

        {/* ── Step 3: Emergency Contact ── */}
        <Box px={6} py={8} borderRadius="lg" bg="white" my="2rem">
          <Heading fontSize="md" mb={1}>Emergency Contact Details</Heading>
          <Flex justifyContent="space-between" fontSize="0.875rem" letterSpacing={0} mb={6}>
            <Text color="#90A3BF">Please enter emergency contact</Text>
            <Text color="#90A3BF">Step 3 of 7</Text>
          </Flex>
          <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4} mb={6}>
            <GridItem>
              <LabelInput
                placeholder="Enter Emergency Contact Name"
                label="Emergency Contact Name"
                name="emergencyContactName"
                value={values.emergencyContactName}
                onChange={handleChange}
                onBlur={handleBlur}
                bg={values.emergencyContactName ? "gray.100" : "white"}
              />
              <FieldError error={errors.emergencyContactName} touched={touched.emergencyContactName} />
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="Enter Emergency Contact Number"
                label="Emergency Contact Number"
                name="emergencyContactNumber"
                value={values.emergencyContactNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                inputMode="numeric"
                bg={values.emergencyContactNumber ? "gray.100" : "white"}
              />
              <FieldError error={errors.emergencyContactNumber} touched={touched.emergencyContactNumber} />
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="e.g., Father, Spouse, Sibling"
                label="Emergency Contact Relationship"
                name="emergencyContactRelationship"
                value={values.emergencyContactRelationship}
                onChange={handleChange}
                onBlur={handleBlur}
                bg={values.emergencyContactRelationship ? "gray.100" : "white"}
              />
              <FieldError error={errors.emergencyContactRelationship} touched={touched.emergencyContactRelationship} />
            </GridItem>
          </Grid>
        </Box>

        {/* ── Step 4: License & Documentation ── */}
        <Box px={6} py={8} borderRadius="lg" bg="white" my="2rem">
          <Heading fontSize="md" mb={1}>License & Documentation</Heading>
          <Flex justifyContent="space-between" fontSize="0.875rem" letterSpacing={0} mb={6}>
            <Text color="#90A3BF">Please enter license details</Text>
            <Text color="#90A3BF">Step 4 of 7</Text>
          </Flex>

          <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4} mb={6}>
            <GridItem>
              <LabelInput
                placeholder="e.g., DL05 20230012345"
                label="Driving License Number"
                name="drivingLicenseNumber"
                isRequired
                value={values.drivingLicenseNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                bg={values.drivingLicenseNumber ? "gray.100" : "white"}
              />
              <FieldError error={errors.drivingLicenseNumber} touched={touched.drivingLicenseNumber} />
              {!errors.drivingLicenseNumber && (
                <Text fontSize="xs" color="#90A3BF" mt={1}>Format: DL05 20230012345</Text>
              )}
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="Enter License Expiry Date"
                label="License Expiry Date"
                name="drivingLicenseExpiryDate"
                type="date"
                value={values.drivingLicenseExpiryDate}
                onChange={handleChange}
                onBlur={handleBlur}
                bg={values.drivingLicenseExpiryDate ? "gray.100" : "white"}
                min={TODAY.toISOString().split("T")[0]}
              />
              <FieldError error={errors.drivingLicenseExpiryDate} touched={touched.drivingLicenseExpiryDate} />
            </GridItem>

            <GridItem>
              <FormLabel fontSize="1rem" color="#1A202C" htmlFor="drivingLicenseType">
                Driving License Type
              </FormLabel>
              <Select
                placeholder="License Type"
                name="drivingLicenseType"
                id="drivingLicenseType"
                value={values.drivingLicenseType}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outline"
                borderRadius="lg"
                bg={values.drivingLicenseType ? "gray.100" : "white"}
                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                w="100%"
                h="56px"
              >
                <option value="LMV">LMV</option>
                <option value="HMV">HMV</option>
              </Select>
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="e.g., 123456789012"
                label="Aadhaar Card Number"
                name="aadhaarCardNumber"
                isRequired
                value={values.aadhaarCardNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                inputMode="numeric"
                bg={values.aadhaarCardNumber ? "gray.100" : "white"}
              />
              <FieldError error={errors.aadhaarCardNumber} touched={touched.aadhaarCardNumber} />
              {!errors.aadhaarCardNumber && (
                <Text fontSize="xs" color="#90A3BF" mt={1}>Must be exactly 12 digits</Text>
              )}
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="e.g., ABCDE1234F"
                label="PAN Card Number"
                name="panCardNumber"
                isRequired
                value={values.panCardNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                bg={values.panCardNumber ? "gray.100" : "white"}
              />
              <FieldError error={errors.panCardNumber} touched={touched.panCardNumber} />
              {!errors.panCardNumber && (
                <Text fontSize="xs" color="#90A3BF" mt={1}>Format: ABCDE1234F</Text>
              )}
            </GridItem>
          </Grid>

          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
            <GridItem>
              <Checkbox
                name="licenseHillEndorsement"
                isChecked={values.licenseHillEndorsement}
                onChange={(e) => setFieldValue("licenseHillEndorsement", e.target.checked)}
              >
                Hill Endorsement
              </Checkbox>
            </GridItem>
            <GridItem>
              <Checkbox
                name="isVerified"
                isChecked={values.isVerified}
                onChange={(e) => setFieldValue("isVerified", e.target.checked)}
              >
                Verified Driver
              </Checkbox>
            </GridItem>
          </Grid>
        </Box>

        {/* ── Step 5: Additional Details ── */}
        <Box px={6} py={8} borderRadius="lg" bg="white" my="2rem">
          <Heading fontSize="md" mb={1}>Additional Details</Heading>
          <Flex justifyContent="space-between" fontSize="0.875rem" letterSpacing={0} mb={6}>
            <Text color="#90A3BF">Please enter additional information</Text>
            <Text color="#90A3BF">Step 5 of 7</Text>
          </Flex>
          <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4} mb={6} alignItems="flex-end">
            <GridItem>
              <FormControl>
                <FormLabel fontSize="1rem" color="#1A202C">Driver Experience (in years)</FormLabel>
                <Input
                  placeholder="e.g., 5"
                  name="driverExperience"
                  value={values.driverExperience}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(0, 2);
                    setFieldValue("driverExperience", v);
                    setFieldTouched("driverExperience", true, false);
                  }}
                  onBlur={handleBlur}
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  variant="filled"
                  borderRadius="lg"
                  h="56px"
                  bg={values.driverExperience ? "gray.100" : "white"}
                  isInvalid={!!errors.driverExperience && touched.driverExperience}
                />
              </FormControl>
              <FieldError error={errors.driverExperience} touched={touched.driverExperience} />
            </GridItem>

            <GridItem>
              <FormLabel fontSize="1rem" color="#1A202C" htmlFor="languagesKnown">Languages Known</FormLabel>
              <Select
                placeholder="Select Languages"
                name="languagesKnown"
                id="languagesKnown"
                value={values.languagesKnown}
                onChange={handleChange}
                onBlur={handleBlur}
                variant="outline"
                borderRadius="lg"
                bg={values.languagesKnown ? "gray.100" : "white"}
                _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                w="100%"
                h="56px"
              >
                <option value="English">English</option>
                <option value="Hindi">Hindi</option>
                <option value="Both">Both (English & Hindi)</option>
              </Select>
              <FieldError error={errors.languagesKnown} touched={touched.languagesKnown} />
            </GridItem>

            <GridItem>
              <LabelInput
                placeholder="Enter Education Details"
                label="Education (Highest Qualification)"
                name="education"
                value={values.education}
                onChange={handleChange}
                onBlur={handleBlur}
                bg={values.education ? "gray.100" : "white"}
              />
              <FieldError error={errors.education} touched={touched.education} />
            </GridItem>
          </Grid>
        </Box>

        {/* ── Step 6: Upload Documents ── */}
        <Box px={6} py={8} borderRadius="lg" bg="white" my="2rem">
          <Heading fontSize="lg" mb={1} color="#1A202C">Upload Documents</Heading>
          <Flex justifyContent="space-between" fontSize="0.875rem" letterSpacing={0} mb={6}>
            <Text color="#90A3BF">Please upload required documents</Text>
            <Text color="#90A3BF">Step 6 of 7</Text>
          </Flex>

          <Box mb={6}>
            <Heading fontSize="md" mb={3} color="#1A202C">Driving License</Heading>
            <Text fontSize="sm" color="#90A3BF" mb={4}>Upload both front and back sides</Text>
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
              <GridItem>
                <InputAndDragAndDrop title="Driving License Front" name="drivingLicenseFront" url={values.drivingLicenseFront} setData={setData} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
                {errors.drivingLicenseFront && formik.submitCount > 0 && (
                  <Text color="red.500" fontSize="xs">{errors.drivingLicenseFront}</Text>
                )}
              </GridItem>
              <GridItem>
                <InputAndDragAndDrop title="Driving License Back" name="drivingLicenseBack" url={values.drivingLicenseBack} setData={setData} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
                {errors.drivingLicenseBack && formik.submitCount > 0 && (
                  <Text color="red.500" fontSize="xs">{errors.drivingLicenseBack}</Text>
                )}
              </GridItem>
            </Grid>
          </Box>

          <Box mb={6}>
            <Heading fontSize="md" mb={3} color="#1A202C">Aadhaar Card</Heading>
            <Text fontSize="sm" color="#90A3BF" mb={4}>Upload both front and back sides</Text>
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
              <GridItem>
                <InputAndDragAndDrop title="Aadhaar Card Front" name="aadhaarCardFront" url={values.aadhaarCardFront} setData={setData} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
                {errors.aadhaarCardFront && formik.submitCount > 0 && (
                  <Text color="red.500" fontSize="xs">{errors.aadhaarCardFront}</Text>
                )}
              </GridItem>
              <GridItem>
                <InputAndDragAndDrop title="Aadhaar Card Back" name="aadhaarCardBack" url={values.aadhaarCardBack} setData={setData} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
                {errors.aadhaarCardBack && formik.submitCount > 0 && (
                  <Text color="red.500" fontSize="xs">{errors.aadhaarCardBack}</Text>
                )}
              </GridItem>
            </Grid>
          </Box>

          <Box mb={6}>
            <Heading fontSize="md" mb={3} color="#1A202C">PAN Card</Heading>
            <Text fontSize="sm" color="#90A3BF" mb={4}>Upload front side</Text>
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
              <GridItem>
                <InputAndDragAndDrop title="PAN Card Front" name="panCardFront" url={values.panCardFront} setData={setData} setFieldValue={setFieldValue} setFieldTouched={setFieldTouched} />
                {errors.panCardFront && formik.submitCount > 0 && (
                  <Text color="red.500" fontSize="xs">{errors.panCardFront}</Text>
                )}
              </GridItem>
            </Grid>
          </Box>

          {/* ── Step 7: Driver Status ── */}
          <Box px={6} py={8} borderRadius="lg" bg="white" my="2rem">
            <Heading fontSize="lg" mb={1} color="#1A202C">Driver Status</Heading>
            <Flex justifyContent="space-between" fontSize="0.875rem" letterSpacing={0} mb={6}>
              <Text color="#90A3BF">Set operational and account status</Text>
              <Text color="#90A3BF">Step 7 of 7</Text>
            </Flex>

            <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4}>
              <GridItem>
                <FormLabel fontSize="1rem" color="#1A202C" htmlFor="operationalStatus">Operational Status</FormLabel>
                <Select
                  name="operationalStatus"
                  id="operationalStatus"
                  value={values.operationalStatus}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outline"
                  borderRadius="lg"
                  bg={values.operationalStatus ? "gray.100" : "white"}
                  _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                  w="100%"
                  h="56px"
                >
                  {DRIVER_OPERATIONAL_STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {DRIVER_OPERATIONAL_STATUS_LABEL[status]}
                    </option>
                  ))}
                </Select>
                <FieldError error={errors.operationalStatus} touched={touched.operationalStatus} />
              </GridItem>

              <GridItem>
                <FormLabel fontSize="1rem" color="#1A202C" htmlFor="accountStatus">Account Status</FormLabel>
                <Select
                  name="accountStatus"
                  id="accountStatus"
                  value={values.accountStatus}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outline"
                  borderRadius="lg"
                  bg={values.accountStatus ? "gray.100" : "white"}
                  _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                  w="100%"
                  h="56px"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                  <option value="blacklisted">Blacklisted</option>
                </Select>
                <FieldError error={errors.accountStatus} touched={touched.accountStatus} />
              </GridItem>
            </Grid>
          </Box>

          <Flex justifyContent="flex-end" gap={4} mt={8}>
            <Link to="/fleet-management/driver">
              <Button
                type="reset"
                variant="outline"
                borderRadius="md"
                border="2px solid #396AFF"
                px={10}
              >
                <Text color="#396AFF">Cancel</Text>
              </Button>
            </Link>
            <Button
              type="submit"
              bg="#396AFF"
              borderRadius="md"
              px={10}
              color="white"
              _hover={{ bg: "#2c54cc" }}
              isLoading={formik.isSubmitting}
              loadingText="Submitting..."
            >
              Submit
            </Button>
          </Flex>
        </Box>
      </form>
    </Box>
  );
};

export default Form;
